import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { toast } from "sonner";
import {
  fetchMe,
  login as loginApi,
  logout as logoutApi,
  registerAuthHandlers,
  type Me,
} from "./api";

type AuthContextValue = {
  me: Me | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });

  const onUnauthorized = useCallback(() => {
    queryClient.setQueryData(["me"], null);
    queryClient.clear();
    navigate({ to: "/login" });
  }, [queryClient, navigate]);

  useEffect(() => {
    registerAuthHandlers({ onUnauthorized });
  }, [onUnauthorized]);

  const loginMutation = useMutation({
    mutationFn: (vars: { email: string; password: string }) =>
      loginApi(vars.email, vars.password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSettled: () => {
      queryClient.setQueryData(["me"], null);
      queryClient.clear();
      navigate({ to: "/login" });
    },
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      me: (meQuery.data as Me | undefined) ?? null,
      isLoading: meQuery.isPending,
      login: async (email, password) => {
        try {
          await loginMutation.mutateAsync({ email, password });
        } catch (err) {
          const ax = err as AxiosError<{ message?: string }>;
          const msg = ax.response?.data?.message ?? "Login fehlgeschlagen";
          toast.error(msg);
          throw err;
        }
      },
      logout: () => logoutMutation.mutateAsync(),
      refresh: () => {
        void queryClient.invalidateQueries({ queryKey: ["me"] });
      },
    }),
    [meQuery.data, meQuery.isPending, loginMutation, logoutMutation, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
